import React, { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash2, Upload, X, Link as LinkIcon,
  Images, Save, ExternalLink, Calendar, MapPin,
} from "lucide-react";

const EMPTY_EVENT = {
  slug: "",
  title: "",
  date: "",
  date_label: "",
  location: "",
  cover_image: "",
  excerpt: "",
  body: "",
  press_release: "",
  gallery: [],
  is_published: true,
  is_past: false,
};
const MAX_UPLOAD_MB = 10;

const slugify = (s) =>
  s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | event object
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState({ done: 0, total: 0 });
  const [selected, setSelected] = useState(new Set()); // selected gallery image paths
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const coverRef = useRef();
  const galleryRef = useRef();

  const load = () =>
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_EVENT);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startEdit = (e) => {
    setEditing(e);
    setForm({
      slug: e.slug || "",
      title: e.title || "",
      date: e.date || "",
      date_label: e.date_label || "",
      location: e.location || "",
      cover_image: e.cover_image || "",
      excerpt: e.excerpt || "",
      body: e.body || "",
      press_release: e.press_release || "",
      gallery: e.gallery || [],
      is_published: e.is_published !== false,
      is_past: e.is_past === true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_EVENT); };

  const uploadCover = async (ev) => {
    const file = ev.target.files?.[0];
    if (coverRef.current) coverRef.current.value = "";
    if (!file) return;
    if (file.size / (1024 * 1024) > MAX_UPLOAD_MB) {
      toast.error(`File troppo grande. Max ${MAX_UPLOAD_MB} MB.`); return;
    }
    setCoverUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setForm((f) => ({ ...f, cover_image: data.path }));
      toast.success("Cover caricata");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload fallito");
    } finally { setCoverUploading(false); }
  };

  const uploadGalleryFiles = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (galleryRef.current) galleryRef.current.value = "";
    if (!files.length) return;

    const eventId = editing && editing !== "new" ? editing.id : null;
    setGalleryUploading(true);
    setGalleryProgress({ done: 0, total: files.length });
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.size / (1024 * 1024) > MAX_UPLOAD_MB) {
        toast.error(`${f.name}: troppo grande, saltato`);
        continue;
      }
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
        });
        uploaded.push(data.path);
      } catch (err) {
        toast.error(`${f.name}: errore`);
      }
      setGalleryProgress({ done: i + 1, total: files.length });
    }
    // Append in local form state
    setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...uploaded] }));
    // If editing an existing event, persist immediately
    if (eventId && uploaded.length) {
      try {
        await api.post(`/admin/events/${eventId}/gallery`, { images: uploaded });
      } catch { /* persisted via save anyway */ }
    }
    setGalleryUploading(false);
    toast.success(`${uploaded.length} foto caricate`);
  };

  const removeGalleryImage = async (src) => {
    if (!window.confirm("Vuoi davvero eliminare questa foto dalla gallery?")) return;
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((x) => x !== src) }));
    const eventId = editing && editing !== "new" ? editing.id : null;
    if (eventId) {
      try {
        await api.delete(`/admin/events/${eventId}/gallery`, { params: { image: src } });
        toast.success("Foto eliminata");
      } catch {
        toast.error("Errore eliminazione — ricarica la pagina");
      }
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.date.trim()) {
      toast.error("Titolo e data sono obbligatori."); return;
    }
    if (!form.slug.trim()) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/events", payload);
        toast.success("Evento creato");
      } else {
        await api.put(`/admin/events/${editing.id}`, payload);
        toast.success("Evento aggiornato");
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Eliminare l'evento "${e.title}"?`)) return;
    try {
      await api.delete(`/admin/events/${e.id}`);
      toast.success("Evento eliminato");
      await load();
    } catch (err) {
      toast.error("Errore eliminazione");
    }
  };

  const publicUrl = (e) => `${window.location.origin}/moto/eventi/${e.slug}`;
  const copyLink = async (e) => {
    try { await navigator.clipboard.writeText(publicUrl(e)); toast.success("Link copiato"); }
    catch { toast.error(publicUrl(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="label-eyebrow text-gray-500">Gestione</span>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Eventi</h1>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-orange label-eyebrow px-5 py-3 inline-flex items-center gap-2" data-testid="event-new-btn">
            <Plus size={14} /> Nuovo evento
          </button>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <div className="card-flat border-2 border-[color:var(--nsa-orange)] p-6 md:p-8 mb-10 space-y-5" data-testid="event-editor">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="label-eyebrow text-[color:var(--nsa-orange)]">{editing === "new" ? "Nuovo" : "Modifica"}</span>
              <h2 className="font-display uppercase text-2xl md:text-3xl mt-1">{form.title || "Nuovo evento"}</h2>
            </div>
            <button onClick={cancel} className="text-sm text-gray-600 hover:text-red-600 inline-flex items-center gap-1">
              <X size={14} /> Chiudi
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Titolo*</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3" data-testid="event-title" />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Slug URL* <span className="text-gray-400 normal-case tracking-normal">(/moto/eventi/{form.slug || "…"})</span></label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="w-full border border-gray-300 outline-none px-3 py-3 font-mono text-sm" data-testid="event-slug" />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Data* <span className="text-gray-400 normal-case tracking-normal">(YYYY-MM-DD)</span></label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-gray-300 px-3 py-3" data-testid="event-date" />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Data visualizzata</label>
              <input value={form.date_label} onChange={(e) => setForm({ ...form, date_label: e.target.value })} placeholder="Es. Domenica 4 Ottobre 2026" className="w-full border border-gray-300 outline-none px-3 py-3" />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow text-gray-500 block mb-2">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Es. Pista Enduro Scalvina — Ponte Nossa (BG)" className="w-full border border-gray-300 outline-none px-3 py-3" />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow text-gray-500 block mb-2">Estratto <span className="text-gray-400 normal-case tracking-normal">(riassunto card)</span></label>
              <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-gray-300 outline-none px-3 py-3" />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow text-gray-500 block mb-2">Corpo (markdown) <span className="text-gray-400 normal-case tracking-normal">**grassetto**, ## sottotitoli, - liste</span></label>
              <textarea rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full border border-gray-300 outline-none px-3 py-3 font-mono text-sm min-h-[260px]" />
            </div>
            <div className="md:col-span-2">
              <label className="label-eyebrow text-gray-500 block mb-2">Comunicato stampa (markdown) <span className="text-gray-400 normal-case tracking-normal">— visibile tramite modale nel box laterale</span></label>
              <textarea rows={10} value={form.press_release} onChange={(e) => setForm({ ...form, press_release: e.target.value })} className="w-full border border-gray-300 outline-none px-3 py-3 font-mono text-sm min-h-[220px]" data-testid="event-press-release" />
            </div>
          </div>

          {/* Cover image */}
          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">Cover evento</label>
            <div className="flex items-start gap-4 flex-wrap">
              <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" />
              <button onClick={() => coverRef.current?.click()} disabled={coverUploading} className="border border-gray-300 px-4 py-2 label-eyebrow inline-flex items-center gap-2 hover:border-gray-900">
                <Upload size={14} /> {coverUploading ? "Caricamento…" : (form.cover_image ? "Sostituisci cover" : "Carica cover")}
              </button>
              {form.cover_image && (
                <div className="relative">
                  <img src={mediaUrl(form.cover_image)} alt="cover" className="h-24 w-32 object-cover border border-gray-300" />
                  <button onClick={() => setForm({ ...form, cover_image: "" })} className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow"><X size={12} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Gallery */}
          <div className="border-t border-gray-200 pt-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <label className="label-eyebrow text-gray-500 flex items-center gap-2"><Images size={14} /> Gallery ({form.gallery?.length || 0} foto)</label>
              <div className="flex gap-2">
                <input ref={galleryRef} type="file" accept="image/*" multiple onChange={uploadGalleryFiles} className="hidden" />
                <button onClick={() => galleryRef.current?.click()} disabled={galleryUploading} className="btn-orange label-eyebrow px-4 py-2 inline-flex items-center gap-2 text-sm disabled:opacity-60">
                  <Upload size={14} /> {galleryUploading ? `Caricamento ${galleryProgress.done}/${galleryProgress.total}…` : "Aggiungi foto (multiple)"}
                </button>
              </div>
            </div>
            {form.gallery?.length > 0 ? (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {form.gallery.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative aspect-square bg-gray-100 group">
                    <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    <button
                      onClick={() => removeGalleryImage(src)}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-lg transition-colors z-10"
                      title="Elimina questa foto"
                      data-testid={`gallery-remove-${i}`}
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                    <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">#{i + 1}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Nessuna foto in gallery. Carica le foto dell'evento.</p>
            )}
          </div>

          <div className="flex items-center gap-5 flex-wrap">
            <label className="label-eyebrow text-gray-700 inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /> Pubblicato
            </label>
            <label className="label-eyebrow text-gray-700 inline-flex items-center gap-2 text-sm" title="Nasconde il form contatti e mostra il banner di ringraziamento">
              <input type="checkbox" checked={form.is_past} onChange={(e) => setForm({ ...form, is_past: e.target.checked })} data-testid="event-is-past" /> Evento passato (hide form)
            </label>
            <button onClick={save} disabled={saving} className="btn-orange label-eyebrow px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="event-save-btn">
              <Save size={14} /> {saving ? "Salvataggio…" : "Salva evento"}
            </button>
            <button onClick={cancel} className="border border-gray-300 label-eyebrow px-6 py-3">Annulla</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {events.length === 0 && !editing && (
          <p className="text-gray-500 italic">Nessun evento creato. Click "Nuovo evento" per iniziare.</p>
        )}
        {events.map((e) => (
          <div key={e.id} className="border border-gray-200 p-4 flex flex-col md:flex-row gap-4 items-start md:items-center" data-testid={`event-row-${e.slug}`}>
            {e.cover_image ? (
              <img src={mediaUrl(e.cover_image)} alt="" className="w-full md:w-24 h-24 object-cover border border-gray-200 flex-shrink-0" />
            ) : (
              <div className="w-full md:w-24 h-24 bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0"><Calendar size={20} /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="text-xs text-gray-500">{e.date_label || e.date}</span>
                {e.is_published === false && <span className="text-[10px] uppercase bg-gray-400 text-white px-2 py-0.5">Bozza</span>}
                <span className="text-[10px] text-gray-500">· {e.gallery?.length || 0} foto</span>
              </div>
              <div className="font-display uppercase text-base md:text-lg">{e.title}</div>
              {e.location && <div className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12} /> {e.location}</div>}
            </div>
            <div className="flex gap-2 md:flex-col md:items-stretch flex-shrink-0 w-full md:w-auto">
              <button onClick={() => copyLink(e)} className="border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-3 py-1.5 text-xs inline-flex items-center gap-1.5 justify-center">
                <LinkIcon size={12} /> Link
              </button>
              <a href={`/moto/eventi/${e.slug}`} target="_blank" rel="noopener noreferrer" className="border border-gray-300 hover:border-gray-900 px-3 py-1.5 text-xs inline-flex items-center gap-1.5 justify-center">
                <ExternalLink size={12} /> Vedi
              </a>
              <button onClick={() => startEdit(e)} className="border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-3 py-1.5 text-xs inline-flex items-center gap-1.5 justify-center" data-testid={`event-edit-${e.slug}`}>
                <Pencil size={12} /> Modifica
              </button>
              <button onClick={() => remove(e)} className="border border-gray-300 hover:border-red-500 hover:text-red-600 px-3 py-1.5 text-xs inline-flex items-center gap-1.5 justify-center">
                <Trash2 size={12} /> Elimina
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
